import { useCallback, useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import { getUnread, runCommunicationCommand } from '../../api/communication';
import type { Unread, UnreadItem } from '../../api/communication';
import { startRealtime, stopRealtime, useRealtime } from '../../realtime';
import { useMe, useSession } from '../../session/useSession';
import { Icon } from '../Icon';
import { PersonBadge, TypePill, when } from './shared';
import { targetRoute } from './targets';

/**
 * La cloche de la barre du haut (planche 137, D67) : les NON LUES seulement
 * (messages, réponses d'étudiants, notifications, alertes de Kelassi), en temps
 * réel. Une notification lue n'y revient plus ; « Toutes les notifications »
 * ouvre l'écran complet.
 *
 * Elle porte aussi le cycle de vie du client temps réel : démarré quand elle se
 * monte (dans la coquille, donc après la connexion), arrêté quand elle se démonte.
 */
export function Bell(): ReactNode {
    const { t, locale } = useSession();
    const me = useMe();
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const [unread, setUnread] = useState<Unread | null>(null);
    const [open, setOpen] = useState(false);
    const [pending, setPending] = useState(false);
    const root = useRef<HTMLDivElement>(null);
    const trigger = useRef<HTMLButtonElement>(null);
    const timer = useRef<number | null>(null);

    const reload = useCallback((): void => {
        getUnread()
            .then(setUnread)
            .catch(() => undefined);
    }, []);

    /** Plusieurs événements arrivent souvent ensemble : une seule relecture. */
    const reloadSoon = useCallback((): void => {
        if (timer.current !== null) window.clearTimeout(timer.current);

        timer.current = window.setTimeout(() => {
            timer.current = null;
            reload();
        }, 300);
    }, [reload]);

    useEffect(() => {
        startRealtime();
        reload();

        return () => {
            stopRealtime();

            if (timer.current !== null) window.clearTimeout(timer.current);
        };
    }, [reload]);

    useRealtime((event) => {
        if (event.type === 'typing') return;

        if (event.type === 'unread') {
            setUnread(event.unread);

            return;
        }

        reloadSoon();
    });

    // La page change : un élément vient peut-être d'être lu (fil ouvert, notification lue).
    useEffect(() => {
        setOpen(false);
        reloadSoon();
    }, [pathname, reloadSoon]);

    useEffect(() => {
        if (!open) return undefined;

        const outside = (event: PointerEvent): void => {
            if (root.current !== null && !root.current.contains(event.target as Node)) setOpen(false);
        };
        const escape = (event: KeyboardEvent): void => {
            if (event.key === 'Escape') {
                setOpen(false);
                trigger.current?.focus();
            }
        };

        document.addEventListener('pointerdown', outside);
        document.addEventListener('keydown', escape);

        return () => {
            document.removeEventListener('pointerdown', outside);
            document.removeEventListener('keydown', escape);
        };
    }, [open]);

    const count = unread?.count ?? 0;
    const items = unread?.items ?? [];
    const label = count === 0 ? t('com.bell.none') : t('com.bell.label', { count });

    async function markAll(): Promise<void> {
        setPending(true);

        try {
            await runCommunicationCommand('notification.read', null, { all: true });

            const alerts = items.filter((item) => item.kind === 'alert').map((item) => item.id);

            if (alerts.length > 0) await runCommunicationCommand('alert.read', null, { ids: alerts });

            const conversations = [...new Set(items.filter((item) => item.kind === 'chat' && item.target.id).map((item) => item.target.id as string))];

            await Promise.all(conversations.map((id) => runCommunicationCommand('chat.read', id, { conversationId: id })));
            setUnread({ count: 0, items: [] });
        } catch {
            // La relecture dira ce qui reste.
        } finally {
            setPending(false);
            reload();
        }
    }

    function go(item: UnreadItem): void {
        setOpen(false);

        if (item.kind === 'alert') {
            void runCommunicationCommand('alert.read', null, { ids: [item.id] })
                .catch(() => undefined)
                .finally(reloadSoon);
        }

        const route = targetRoute(item.target, me.permissions);
        navigate(item.kind === 'reply' && item.target.kind === 'notification' ? `${route}?thread=${encodeURIComponent(item.id)}` : route);
    }

    return (
        <div className="com-bell" ref={root}>
            <button
                ref={trigger}
                className={`icon-button com-bell__button${open ? ' icon-button--active' : ''}`}
                type="button"
                aria-label={label}
                data-tip={open ? undefined : label}
                aria-haspopup="dialog"
                aria-expanded={open}
                onClick={() => {
                    if (!open) reload();

                    setOpen((value) => !value);
                }}
            >
                <Icon name="bell" size={18} />
                {count === 0 ? null : (
                    <span className="com-bell__badge" aria-hidden="true">
                        {count > 99 ? '99+' : count}
                    </span>
                )}
            </button>

            {open ? (
                <div className="com-bell__panel" role="dialog" aria-label={t('com.bell.title')}>
                    <header className="com-bell__head">
                        <strong>{t('com.bell.unread', { count })}</strong>
                        {count === 0 ? null : (
                            <button className="com-link" type="button" disabled={pending} onClick={() => void markAll()}>
                                {t('com.markAllRead')}
                            </button>
                        )}
                    </header>

                    {items.length === 0 ? (
                        <p className="com-bell__empty">
                            <Icon name="check" size={18} />
                            {t('com.bell.empty')}
                        </p>
                    ) : (
                        <ul className="com-bell__list">
                            {items.map((item) => (
                                <li key={`${item.kind}.${item.id}`}>
                                    <button className="com-bell__item" type="button" onClick={() => go(item)}>
                                        <span className="com-bell__avatar">
                                            <PersonBadge person={item.from} />
                                            <span className="com-dot" aria-hidden="true" />
                                        </span>
                                        <span className="com-bell__text">
                                            <span className="com-bell__line">
                                                <strong className="com-ellipsis">{item.title}</strong>
                                                <span className="com-when">{when(item.at, locale, t)}</span>
                                            </span>
                                            <span className="com-bell__context">
                                                {item.kind === 'chat' ? <TypePill tone="ok" label={t('com.type.message')} /> : <TypePill type={item.type} />}
                                                {item.context === null || item.context === item.title || item.context === item.type?.label ? null : <span className="com-ellipsis">{item.context}</span>}
                                            </span>
                                            {item.excerpt === null ? null : <span className="com-bell__excerpt com-ellipsis">{item.excerpt}</span>}
                                        </span>
                                    </button>
                                </li>
                            ))}
                        </ul>
                    )}

                    <footer className="com-bell__foot">
                        <span className="muted">{t('com.bell.hint')}</span>
                        <Link className="com-link" to="/notifications" onClick={() => setOpen(false)}>
                            {t('com.bell.all')}
                        </Link>
                    </footer>
                </div>
            ) : null}
        </div>
    );
}
